import type { ChangeEvent, FC } from 'react';
import { useCallback, useState } from 'react';
import ChevronDownIcon from '@untitled-ui/icons-react/build/esm/ChevronDown';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SvgIcon from '@mui/material/SvgIcon';

import { usePopover } from 'src/hooks/use-popover';

interface MultiSelectProps {
  label: string;
  onChange?: (value: any[]) => void;
  options: { label: string; value: unknown }[];
  value: any[];
  isMulti?: boolean;
}

export const MultiSelect: FC<MultiSelectProps> = (props) => {
  const { label, onChange, options, value, isMulti = [], ...other } = props;
  const popover = usePopover<HTMLButtonElement>();

  const [menuValue, setMenuValue] = useState(value);

  const handleValueChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement> | { target: { checked: boolean; value: string } }
    ): void => {
      let newValue = isMulti ? [...menuValue] : [];

      const eventValue = event.target.value;

      if (event.target.checked) {
        if (!newValue.includes(eventValue)) {
          newValue.push(eventValue);
        }
      } else {
        newValue = newValue.filter((item) => item !== eventValue);
      }

      onChange?.(newValue);
      setMenuValue(newValue);

      if (!isMulti) {
        popover.handleClose();
      }
    },
    [onChange, menuValue, isMulti]
  );

  return (
    <>
      <Button
        color="inherit"
        endIcon={
          <SvgIcon>
            <ChevronDownIcon />
          </SvgIcon>
        }
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        {...other}
      >
        {label}
      </Button>
      <Menu
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
        PaperProps={{ style: { width: 250 } }}
      >
        {options?.map((option) => (
          <>
            {isMulti ? (
              <MenuItem key={option.label}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={menuValue.includes(option.value)}
                      onChange={handleValueChange}
                      value={option.value}
                    />
                  }
                  label={option.label}
                  sx={{
                    flexGrow: 1,
                    mr: 0,
                  }}
                />
              </MenuItem>
            ) : (
              <MenuItem
                sx={{
                  background: menuValue.includes(option.value) ? 'rgba(99, 102, 241, 0.08)' : '',
                }}
                onClick={() => {
                  handleValueChange({
                    target: {
                      checked: !menuValue.includes(option.value),
                      value: option.value as string,
                    },
                  });
                }}
              >
                {option.label}
              </MenuItem>
            )}
          </>
        ))}
      </Menu>
    </>
  );
};